<template>
  <template v-if="sucessEmailUpdated">
    <the-confirm-price-reservation :status="status" />
  </template>
  <template v-else-if="emailUpdated && status === 'loaded'">
    <the-email-already-update />
  </template>
  <div v-else id="supplier-modification" class="container-fluid item-list pb-16">
    <div v-if="status === 'loading'" class="py-64 text-center">
      <v-loader />
    </div>
    <div v-else class="items-content mt-16 px-16">
      <div
        v-for="(supplierBlock, blockIndex) in supplierData"
        :key="blockIndex"
        class="bg-white rounded shadow-sm border overflow-hidden mb-16"
      >
        <div class="border-bottom p-16 bg-light d-flex align-items-center">
          <div class="supplier-avatar rounded-sm bg-primary me-12 text-white fw-bold">
            {{ capitalizeFirstLetter(supplierBlock.name) }}
          </div>
          <div>
            <div class="fw-bold text-dark">{{ supplierBlock.name }}</div>
            <div class="text-xs text-muted">{{ $t("supplier.portal.priceTooltip") }}</div>
          </div>
        </div>

        <div class="table-responsive">
          <table class="table table-hover align-middle mb-0 table-compact modification-table">
            <thead class="bg-light border-bottom">
              <tr>
                <th class="ps-16 py-12 text-xs fw-bold text-uppercase text-muted">{{ $t("general.shared.name") }}</th>
                <th class="px-12 py-12 text-xs fw-bold text-uppercase text-muted">{{ $t("general.shared.checkIn") }}</th>
                <th v-if="isHotelSupplier(supplierBlock)" class="px-12 py-12 text-xs fw-bold text-uppercase text-muted">{{ $t("general.shared.checkOut") }}</th>
                <th v-if="isHotelSupplier(supplierBlock)" class="px-12 py-12 text-xs fw-bold text-uppercase text-muted text-center">{{ $t("supplier.portal.nights") }}</th>
                <th class="px-12 py-12 text-xs fw-bold text-uppercase text-muted text-center">{{ $t("supplier.portal.details") }}</th>
                <th class="px-12 py-12 text-xs fw-bold text-uppercase text-muted text-center">
                  <div class="d-flex align-items-center justify-content-center">
                    {{ $t("supplier.portal.price") }}
                    <v-icon
                      v-tooltip
                      icon="info"
                      size="xs"
                      class="ms-4 text-muted cursor-help"
                      :title="$t('supplier.portal.priceTooltip')"
                    />
                  </div>
                </th>
                <th class="px-12 py-12 text-xs fw-bold text-uppercase text-muted text-center">{{ $t("supplier.portal.total") }}</th>
                <th class="pe-16 py-12 text-xs fw-bold text-uppercase text-muted text-center">{{ $t("supplier.portal.status") }}</th>
              </tr>
            </thead>
            <tbody class="group-tbody">
              <template v-for="(supplier, supplierIndex) in supplierBlock.suppliers" :key="supplierIndex">
                <template v-for="item in supplier.itemResourcesGrouped" :key="item.uiKey">
                  <tr>
                    <td class="ps-16">
                      <div class="d-flex align-items-center">
                        <button
                          v-if="item.resources.length > 1"
                          class="expander-btn me-8"
                          type="button"
                          @click="toggleExpand(item.uiKey)"
                        >
                          <v-icon :icon="expanded[item.uiKey] ? 'chevron-up' : 'chevron-down'" size="sm" />
                        </button>
                        <span class="text-sm fw-medium text-dark">{{ supplierNameByLange(item.translations) }}</span>
                      </div>
                    </td>
                    <td>
                      <span class="text-sm text-dark">{{ $str.formatDateTime(item.date, settings.formatDate) }}</span>
                    </td>
                    <td v-if="isHotelSupplier(supplierBlock)">
                      <span class="text-sm text-dark">{{ $str.formatDateTime(item.endDate, settings.formatDate) }}</span>
                    </td>
                    <td v-if="isHotelSupplier(supplierBlock)" class="text-center">
                      <span class="text-sm text-dark">{{ calculateNights(item.date, item.endDate) }}</span>
                    </td>
                    <td class="text-center">
                      <div class="text-xs text-muted">
                        <span class="fw-bold text-dark">{{ $t("supplier.portal.quantity") }}:</span> {{ item.quantity }}
                        <template v-if="item.paxes.length">
                          <span class="mx-4">|</span>
                          <span class="fw-bold text-dark">{{ $t("supplier.portal.pax") }}:</span> {{ item.paxes.length }}
                        </template>
                      </div>
                    </td>
                    <td>
                      <div class="d-flex align-items-center justify-content-end">
                        <div class="price-edit-wrapper" :class="{ 'is-editing': editingKey === item.uiKey, 'is-updated': isPriceChanged(item) }">
                          <input
                            :ref="(el) => setPriceInputRef(el, item.uiKey)"
                            v-model="item.editablePrice"
                            type="number"
                            step="0.01"
                            min="0"
                            :readonly="editingKey !== item.uiKey"
                            class="price-v2-input"
                            @blur="finishPriceEdit(item)"
                            @keyup.enter="finishPriceEdit(item)"
                          />
                          <button
                            v-if="editingKey !== item.uiKey"
                            v-tooltip
                            class="btn-edit-price"
                            type="button"
                            :title="$t('supplier.portal.editPriceTooltip')"
                            @click="startPriceEdit(item)"
                          >
                            <v-icon icon="pencil" size="xs" />
                          </button>
                        </div>
                      </div>
                    </td>
                    <td class="text-center">
                      <span class="text-sm fw-bold text-dark">{{ formatPrice(roundPrice(item.netPrice * item.quantity)) }}</span>
                    </td>
                    <td class="pe-16">
                      <div class="d-flex align-items-center justify-content-center gap-4">
                        <span
                          v-tooltip
                          :class="getStatusClass(item.reservationStatus)"
                          class="badge-status-dot"
                          :title="`${$t('supplier.portal.bookingStatus')}: ${item.reservationStatus}`"
                        >
                          <v-icon :icon="getStatusIcon(item.reservationStatus)" size="xs" />
                        </span>
                        <span
                          v-tooltip
                          :class="getStatusClass(item.paymentStatus)"
                          class="badge-status-dot"
                          :title="`${$t('supplier.portal.paymentStatus')}: ${item.paymentStatus}`"
                        >
                          <v-icon :icon="getStatusIcon(item.paymentStatus)" size="xs" />
                        </span>
                        <span
                          v-tooltip
                          :class="getStatusClass(item.invoiceStatus)"
                          class="badge-status-dot"
                          :title="`${$t('supplier.portal.invoiceStatus')}: ${item.invoiceStatus}`"
                        >
                          <v-icon :icon="getStatusIcon(item.invoiceStatus)" size="xs" />
                        </span>
                      </div>
                    </td>
                  </tr>
                  <tr v-if="expanded[item.uiKey] && item.resources.length > 1" class="sub-resources-row">
                    <td :colspan="isHotelSupplier(supplierBlock) ? 8 : 6" class="p-0">
                      <table class="table mb-0 sub-table">
                        <tbody>
                          <tr v-for="subResource in item.resources" :key="subResource.id" class="bg-light-soft">
                            <td class="ps-32">
                              <span class="text-xs text-muted">{{ supplierNameByLange(subResource.translations) }}</span>
                            </td>
                            <td>
                              <span class="text-xs text-muted">{{ $str.formatDateTime(subResource.date, settings.formatDate) }}</span>
                            </td>
                            <td v-if="isHotelSupplier(supplierBlock)">
                              <span class="text-xs text-muted">{{ $str.formatDateTime(subResource.endDate, settings.formatDate) }}</span>
                            </td>
                            <td v-if="isHotelSupplier(supplierBlock)" class="text-center">
                              <span class="text-xs text-muted">{{ calculateNights(subResource.date, subResource.endDate) }}</span>
                            </td>
                            <td class="text-center">
                              <span class="text-xs text-muted">x{{ subResource.quantity }}</span>
                            </td>
                            <td class="text-end">
                              <span class="text-xs text-muted">{{ formatPrice(roundPrice(subResource.netPrice)) }}</span>
                            </td>
                            <td colspan="2"></td>
                          </tr>
                        </tbody>
                      </table>
                    </td>
                  </tr>
                </template>
              </template>
            </tbody>
            <tfoot class="bg-light border-top">
              <tr>
                <td
                  :colspan="isHotelSupplier(supplierBlock) ? 6 : 4"
                  class="ps-16 py-12 text-xs fw-bold text-uppercase text-muted"
                >
                  {{ $t("supplier.portal.total") }}
                </td>
                <td class="px-12 py-12 text-center">
                  <span class="text-sm fw-bold text-dark">{{ formatPrice(blockTotal(supplierBlock)) }}</span>
                </td>
                <td class="pe-16 py-12"></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      <div class="bg-white rounded shadow-sm border p-16 mb-16">
        <label for="supplier-observation" class="label-xs text-muted mb-4">{{ $t("emailTemplate.observations") }}</label>
        <textarea
          id="supplier-observation"
          v-model="supplierObservation"
          class="form-control observation-input"
          rows="4"
          :placeholder="$t('emailTemplate.free_text')"
        ></textarea>
      </div>

      <div class="d-flex justify-content-end gap-8">
        <template v-if="hasChanges">
          <v-button
            class="btn-discard text-center cursor-pointer"
            size="sm"
            @click="discardChanges"
          >
            {{ $t("general.pages.discardChanges") }}
          </v-button>
          <v-button
            class="btn-save-confirm text-center cursor-pointer"
            size="sm"
            @click="reviewChanges('modify')"
          >
            {{ $t("general.button.save") }} & {{ $t("general.button.confirm") }}
          </v-button>
        </template>
        <v-button
          v-else
          class="btn-accept text-center cursor-pointer"
          size="sm"
          @click="reviewChanges('accept')"
        >
          {{ $t("general.shared.accept") }}
        </v-button>
      </div>
    </div>

    <the-editable-info-modal
      v-if="showModal && status === 'loaded'"
      info-key="general.shared.confirmPriceInfoV2"
      @closed="closeModal"
    />

    <v-modal
      id="show-check-0-price-modal"
      class-name="modal-fullscreen-sm-down"
      v-if="showPricingConfirmation"
      size="lg"
      @closed="showPricingConfirmation = false"
    >
      <modal-header>
        <modal-title>{{ $t("emailTemplate.email_warning") }}</modal-title>
      </modal-header>
      <modal-body>
        <div>
          {{ $t("emailTemplate.price_warning") }}
        </div>
      </modal-body>
      <modal-footer>
        <v-button
          class="btn-sm btn-success align-self-end cursor-pointer"
          size="sm"
          variant="success"
          @click="openSummary"
        >
          {{ $t("general.button.ok") }}
        </v-button>
      </modal-footer>
    </v-modal>

    <v-modal
      id="show-summary-modal"
      class-name="modal-fullscreen-sm-down"
      v-if="showSummary"
      size="lg"
      @closed="showSummary = false"
    >
      <modal-header>
        <modal-title>{{ $t("general.pages.reviewSummaryTitle") }}</modal-title>
      </modal-header>
      <modal-body>
        <p class="text-sm text-muted">{{ $t("general.pages.reviewSummaryText") }}</p>
        <div class="table-responsive">
          <table class="table align-middle mb-0 table-compact summary-table">
            <thead class="bg-light border-bottom">
              <tr>
                <th class="ps-16 py-12 text-xs fw-bold text-uppercase text-muted">{{ $t("general.shared.name") }}</th>
                <th class="px-12 py-12 text-xs fw-bold text-uppercase text-muted text-center">{{ $t("supplier.portal.quantity") }}</th>
                <th class="px-12 py-12 text-xs fw-bold text-uppercase text-muted text-center">{{ $t("supplier.portal.price") }}</th>
                <th class="pe-16 py-12 text-xs fw-bold text-uppercase text-muted text-center">{{ $t("supplier.portal.total") }}</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="(supplierBlock, blockIndex) in supplierData" :key="blockIndex">
                <template v-for="(supplier, supplierIndex) in supplierBlock.suppliers" :key="supplierIndex">
                  <tr
                    v-for="item in supplier.itemResourcesGrouped"
                    :key="item.uiKey"
                    :class="{ 'is-updated-row': isPriceChanged(item) }"
                  >
                    <td class="ps-16">
                      <span class="text-sm fw-medium text-dark">{{ supplierNameByLange(item.translations) }}</span>
                    </td>
                    <td class="text-center">
                      <span class="text-sm text-dark">{{ item.quantity }}</span>
                    </td>
                    <td class="text-center">
                      <span class="text-sm text-dark">{{ formatPrice(roundPrice(item.netPrice)) }}</span>
                    </td>
                    <td class="pe-16 text-center">
                      <span class="text-sm fw-bold text-dark">{{ formatPrice(roundPrice(item.netPrice * item.quantity)) }}</span>
                    </td>
                  </tr>
                </template>
              </template>
            </tbody>
          </table>
        </div>
        <div v-if="supplierObservation.trim()" class="mt-16">
          <div class="label-xs text-muted mb-4">{{ $t("emailTemplate.observations") }}</div>
          <div class="text-sm text-dark observation-preview">{{ supplierObservation }}</div>
        </div>
      </modal-body>
      <modal-footer>
        <v-button
          class="btn-sm btn-outline-secondary align-self-end cursor-pointer me-8"
          size="sm"
          variant="secondary"
          @click="showSummary = false"
        >
          {{ $t("general.button.cancel") }}
        </v-button>
        <v-button
          class="btn-sm btn-success align-self-end cursor-pointer"
          size="sm"
          variant="success"
          @click="confirm(summaryMode)"
        >
          {{ $t("general.button.confirm") }}
        </v-button>
      </modal-footer>
    </v-modal>
  </div>
</template>

<script setup lang="ts" name="TheCaseModificationContent">
import _, { cloneDeep } from "lodash";
import { computed, nextTick, onMounted, reactive, ref } from "vue";
import VLoader from "@/components/vendor/loader/VLoader.vue";
import VButton from "@/components/vendor/basic/button/VButton.vue";
import VIcon from "@/components/vendor/basic/icon/VIcon.vue";
import { useAppStore } from "@/stores/app";
import api from "@/api";
import TheConfirmPriceReservation from "@/components/email/TheConfirmPriceReservation.vue";
import TheEmailAlreadyUpdate from "@/components/email/TheEmailAlreadyUpdate.vue";
import TheEditableInfoModal from "@/components/email/TheEditableInfoModal.vue";
import VModal from "@/components/vendor/basic/modal/VModal.vue";
import ModalHeader from "@/components/vendor/basic/modal/ModalHeader.vue";
import ModalTitle from "@/components/vendor/basic/modal/ModalTitle.vue";
import ModalBody from "@/components/vendor/basic/modal/ModalBody.vue";
import ModalFooter from "@/components/vendor/basic/modal/ModalFooter.vue";
import { useRoute } from "vue-router";
import { formatPrice, roundPrice } from "@/composables/pricing";
import { storeToRefs } from "pinia";
import { DateTime } from "luxon";
import { useToast } from "vue-toastification";
import { useI18n } from "vue-i18n";

const { t, locale } = useI18n();
const toast = useToast();
const route = useRoute();
const appStore = useAppStore();
const { settings } = storeToRefs(appStore);
const supplierData = ref<any[]>([]);
const supplierDataBackup = ref<any[]>([]);
const supplierObservation = ref("");
const status = ref("loading");
const sucessEmailUpdated = ref(false);
const emailUpdated = ref<boolean | null>(null);

const showModal = ref(true);
const showPricingConfirmation = ref(false);
const showSummary = ref(false);
const summaryMode = ref<"accept" | "modify">("accept");

const editingKey = ref<string | null>(null);
const priceInputs = ref<Record<string, HTMLInputElement>>({});
const expanded = reactive<Record<string, boolean>>({});

onMounted(async () => {
  status.value = "loading";
  await emailInfoPricing();
  status.value = "loaded";
});

const closeModal = () => {
  showModal.value = false;
};

const supplierNameByLange = (translations: any) => {
  const match = translations?.find((item: any) => item.locale === locale.value);
  return match?.name || translations?.[0]?.name || "";
};

const calculateNights = (initDate: any, endDate: any) => {
  if (!initDate || !endDate) {
    return 0;
  }

  const init = DateTime.fromISO(initDate).startOf("day");
  const end = DateTime.fromISO(endDate).startOf("day");

  return end.diff(init, "days").days;
};

const capitalizeFirstLetter = (str?: string): string => {
  if (!str) return "";
  return str.charAt(0).toUpperCase();
};

const isHotelSupplier = (supplierBlock: any) => {
  return supplierBlock?.suppliers?.some((s: any) => s?.type === 'hotel') ?? false;
};

const allGroupedItems = computed(() =>
  (supplierData.value ?? []).flatMap((block: any) =>
    (block.suppliers ?? []).flatMap((supplier: any) => supplier.itemResourcesGrouped ?? [])
  )
);

const isPriceChanged = (item: any) => {
  return Number(item.netPrice) !== Number(item.originalNetPrice);
};

const blockTotal = (supplierBlock: any) =>
  roundPrice(
    (supplierBlock?.suppliers ?? [])
      .flatMap((supplier: any) => supplier.itemResourcesGrouped ?? [])
      .reduce((sum: number, item: any) => sum + Number(item.netPrice ?? 0) * Number(item.quantity ?? 0), 0)
  );

const hasChanges = computed(() =>
  allGroupedItems.value.some(isPriceChanged) || supplierObservation.value.trim() !== ""
);

const setPriceInputRef = (el: any, uiKey: string) => {
  if (el) {
    priceInputs.value[uiKey] = el;
  }
};

const toggleExpand = (uiKey: string) => {
  expanded[uiKey] = !expanded[uiKey];
};

const startPriceEdit = (item: any) => {
  editingKey.value = item.uiKey;
  nextTick(() => {
    const input = priceInputs.value[item.uiKey];
    if (input) {
      input.focus();
      input.select();
    }
  });
};

const finishPriceEdit = (item: any) => {
  if (editingKey.value !== item.uiKey) return;

  const newPrice = Number(item.editablePrice);
  if (Number.isNaN(newPrice) || newPrice < 0) {
    toast.error(t("general.pages.invalidPrice"));
    item.editablePrice = item.originalNetPrice;
    item.netPrice = item.originalNetPrice;
    editingKey.value = null;
    return;
  }

  item.netPrice = roundPrice(newPrice);
  item.editablePrice = item.netPrice;
  editingKey.value = null;
};

const discardChanges = () => {
  supplierData.value = cloneDeep(supplierDataBackup.value);
  supplierObservation.value = "";
  editingKey.value = null;
};

const reviewChanges = (mode: "accept" | "modify") => {
  const hasZeroSupplierPrice = allGroupedItems.value.some((item: any) => Number(item.netPrice) === 0);

  if (hasZeroSupplierPrice) {
    summaryMode.value = mode;
    showPricingConfirmation.value = true;
  } else {
    summaryMode.value = mode;
    showSummary.value = true;
  }
};

const openSummary = () => {
  showPricingConfirmation.value = false;
  showSummary.value = true;
};

const emailInfoPricing = async () => {
  const { caseid, token, resources } = route.query;
  const resourceIds = typeof resources === "string" ? resources.split(",").map(Number) : [];

  if (caseid) {
    const payload = {
      resourceIds,
    };
    const res = await api.case.fetchModification(caseid, token, payload);
    const gropDAta = res?.content;

    // Step 1: Sort data
    const sortedData = _.sortBy(gropDAta, (item) => item.start_date);

    // Step 2: Group by type
    const groupedByType = _.groupBy(sortedData, (item) => item.type || "unknown");

    // Step 3: Sort types (hotel first)
    const sortedTypes = Object.keys(groupedByType).sort((a, b) => {
      if (a === "hotel") return -1;
      if (b === "hotel") return 1;
      return a.localeCompare(b);
    });

    const pricingItems = sortedTypes
      .map((name) => {
        const caseDayItems = _.sortBy(groupedByType[name], (item) => item.start_date);

        // ---- Supplier Grouping ----
        const supplierGroups: { [key: string]: any[] } = {};
        for (const item of caseDayItems) {
          if (!item.item_data) continue;
          const supplier = item.item_data;

          const key = supplier.type === "hotel" ? `${supplier.id}-${item.case_day_id}` : `${supplier.id}`;
          (supplierGroups[key] ||= []).push(item);
        }

        const suppliersMapped = Object.values(supplierGroups)
          .map((groupItems) => {
            const firstItem = groupItems[0];
            const supplier = firstItem.item_data;

            // skip if NO resources at all across the group
            const anyResources = groupItems.some(
              (it) => Array.isArray(it.case_day_item_resources) && it.case_day_item_resources.length > 0
            );
            if (!anyResources) return null;

            const makeGroupKey = (parentItem: any, resource: any) => {
              const resPayment = resource?.payment_status ?? parentItem?.payment_status ?? "";
              const resBooking = resource?.booking_status ?? parentItem?.booking_status ?? "";
              const resInvoice = resource?.invoice_status ?? parentItem?.invoice_status ?? "";
              const itemType = parentItem.type ?? parentItem.item_data?.type ?? "";
              const supplierResId = resource?.supplier_resource?.id ?? resource?.supplier_resource_id ?? "";

              if (itemType === "hotel") {
                // Hotels: group only when room type, checkin, checkout, net_price, and statuses all match
                const resStart = resource?.checkin_date ?? parentItem?.checkin_date ?? "";
                const checkin = resource?.checkin_date ?? parentItem?.checkin_date;
                const nights = resource?.nights ?? parentItem?.nights ?? 0;
                const resEnd = (checkin && nights)
                  ? DateTime.fromISO(checkin).plus({ days: nights }).toISODate()
                  : "";
                const resNet = roundPrice(resource?.net_price ?? parentItem?.net_price ?? 0);
                return `${supplierResId}-${resStart}-${resEnd}-${resNet}-${resPayment}-${resBooking}-${resInvoice}`;
              }

              if (itemType === "transfer" || itemType === "transport" || itemType === "luggage_transport") {
                // Transfers/transport/luggage: allow grouping across multiple dates, same resource + statuses
                return `${supplierResId}-${resPayment}-${resBooking}-${resInvoice}`;
              }

              // Other services: group by same resource + same checkin date + same statuses
              const resStart = resource?.checkin_date ?? parentItem?.checkin_date ?? "";
              return `${supplierResId}-${resStart}-${resPayment}-${resBooking}-${resInvoice}`;
            };

            const groupMap: Record<string, any[]> = {};

            for (const parentItem of groupItems) {
              const resources = parentItem.case_day_item_resources ?? [];
              for (const res of resources) {
                const key = makeGroupKey(parentItem, res);
                (groupMap[key] ||= []).push({ parentItem, resource: res });
              }
            }

            const itemResourcesGrouped = Object.values(groupMap).map((bucket, bucketIndex) => {
              const dates = bucket
                .map((b: any) => b.resource?.checkin_date ?? b.parentItem?.checkin_date)
                .filter(Boolean);
              const date = dates.length ? dates.sort()[0] : null;
              const endDates = bucket
                .map((b: any) => {
                  const checkout = b.resource?.checkout_date ?? b.parentItem?.checkout_date;
                  if (checkout) return checkout;
                  const checkin = b.resource?.checkin_date ?? b.parentItem?.checkin_date;
                  const nights = b.resource?.nights ?? b.parentItem?.nights ?? 0;
                  if (checkin && nights) {
                    return DateTime.fromISO(checkin).plus({ days: nights }).toISODate();
                  }
                  return null;
                })
                .filter(Boolean);
              const endDate = endDates.length ? endDates.sort()[0] : null;

              const mappedResources = _.sortBy(
                bucket.map(({ parentItem, resource }: any) => ({
                  id: resource.id,
                  netPrice: roundPrice(resource.net_price),
                  netValidated: !!(resource.is_price_validated ?? parentItem.is_price_validated),
                  isUpdatedBySupplier: !!(resource.is_updated_by_supplier ?? false),
                  reservationStatus: resource.booking_status ?? parentItem.booking_status,
                  invoiceStatus: resource.invoice_status ?? parentItem.invoice_status,
                  paymentStatus: resource.payment_status ?? parentItem.payment_status,
                  notes: resource.notes ?? parentItem.notes ?? null,
                  supplierResource: resource.supplier_resource ?? {
                    id: resource.supplier_resource_id ?? null,
                    name: resource.supplier_resource?.name ?? parentItem.item_data?.name ?? null,
                    type: resource.supplier_resource?.type ?? parentItem.item_data?.type ?? null,
                  },
                  supplierResourceName: resource.supplier_resource?.name ?? parentItem.item_data?.name ?? null,
                  date: resource.checkin_date ?? parentItem.checkin_date ?? null,
                  endDate: resource.checkout_date
                    ?? parentItem.checkout_date
                    ?? (resource.checkin_date && resource.nights
                      ? DateTime.fromISO(resource.checkin_date).plus({ days: resource.nights }).toISODate()
                      : (parentItem.checkin_date && parentItem.nights
                        ? DateTime.fromISO(parentItem.checkin_date).plus({ days: parentItem.nights }).toISODate()
                        : null)),
                  quantity: 1,
                  paxes:
                    (resource?.paxes || [])
                      ?.map((p: any) => p.pax?.name || p.name) || [],
                  translations: resource?.supplier_resource?.translations,
                  __raw: { resource, parentItem },
                })),
                (r) => r.date ?? ""
              );

              const totalNetPrice = roundPrice(_.sumBy(mappedResources, "netPrice"));
              // netPrice is the per-unit price (total divided by quantity),
              // totalNetPrice is the sum across all resources in the group.
              // The total price column displays netPrice * quantity.
              const unitNetPrice = bucket.length > 0 ? roundPrice(totalNetPrice / bucket.length) : 0;

              return {
                uiKey: `${firstItem.id}-${bucketIndex}`,
                date,
                endDate,
                resources: mappedResources,
                translations: mappedResources[0]?.translations,
                paxes: mappedResources.flatMap((r: any) => r.paxes || []) as string[],
                supplierResourceId: mappedResources[0]?.supplierResource?.id ?? null,
                name: mappedResources[0]?.supplierResource?.name ?? supplier.name,
                type: mappedResources[0]?.supplierResource?.type ?? supplier.type,
                quantity: bucket.length,
                totalNetPrice,
                netPrice: unitNetPrice,
                originalNetPrice: unitNetPrice,
                editablePrice: unitNetPrice,
                paymentStatus: mappedResources[0]?.paymentStatus ?? null,
                reservationStatus: mappedResources[0]?.reservationStatus ?? null,
                invoiceStatus: mappedResources[0]?.invoiceStatus ?? null,
                netValidated: mappedResources[0]?.netValidated ?? false,
                tourDayItemResourceIds: bucket.map((b: any) => b.resource.id),
                caseDayId: bucket[0].parentItem.case_day_id,
                caseDayItemId: bucket[0].parentItem.id,
              };
            });

            return {
              ...supplier,
              invoiceChecked: false,
              itemResourcesGrouped: _.sortBy(itemResourcesGrouped, (r) => r.date ?? ""),
              totalNetPrice: _.sumBy(itemResourcesGrouped, "totalNetPrice"),
              netValidated: itemResourcesGrouped[0]?.netValidated,
              paymentStatus: itemResourcesGrouped[0]?.paymentStatus,
              invoicePaymentStatus: itemResourcesGrouped.every((r) => r.paymentStatus === "complete")
                ? "complete"
                : "pending",
              reservationStatus: itemResourcesGrouped[0]?.reservationStatus,
              invoiceStatus: itemResourcesGrouped[0]?.invoiceStatus,
              type: supplier.type,
              name: supplier.name,
              caseDayId: firstItem.case_day_id,
              caseDayItemId: firstItem.id,
              note: firstItem.note,
              paxes: firstItem.paxes,
            };
          })
          .filter(Boolean);

        // Sort suppliers by earliest date
        const sortedSuppliers = _.sortBy(suppliersMapped, (s) => s.itemResourcesGrouped?.[0]?.date);

        // Only include this pricing item if there are valid suppliers
        if (sortedSuppliers.length === 0) return null;

        return {
          name,
          suppliers: sortedSuppliers,
        };
      })
      .filter(Boolean);
    const arr: any = [];
    const filterSupplier = pricingItems.filter((item: any) => item.name !== "null");

    filterSupplier.map((item: any) => {
      item?.suppliers?.map((supplier: any) => {
        supplier?.itemResourcesGrouped?.map((itemResource: any) => {
          itemResource?.resources?.map((resource: any) => {
            if (resourceIds.includes(resource.id)) {
              arr.push({ ...item, name: supplier?.name });
            }
          });
        });
      });
    });

    supplierData.value = [{ name: arr[0].name, suppliers: arr[0].suppliers }];
    supplierDataBackup.value = cloneDeep(supplierData.value);

    const anyUpdated = arr.some((item: any) =>
      item?.suppliers?.some((supplier: any) =>
        supplier?.itemResourcesGrouped?.some((group: any) =>
          group?.resources?.some((r: any) => r.isUpdatedBySupplier || r.reservationStatus === 'modified' || r.reservationStatus === 'complete')
        )
      )
    );
    if (anyUpdated) {
      emailUpdated.value = true;
    }
  }
};

const buildSavePayload = () => {
  return allGroupedItems.value
    .filter((item: any) => isPriceChanged(item) && item.resources?.length)
    .map((item: any) => ({
      resourceIds: item.resources.map((r: any) => r.id),
      qty: item.quantity,
      price: roundPrice(Number(item.netPrice) * item.quantity),
    }));
};

const confirm = async (mode: "accept" | "modify") => {
  const { caseid, resources, token } = route.query;

  // Normalize resource IDs → number[]
  const resourceIds = typeof resources === "string" ? resources.split(",").filter(Boolean).map(Number) : [];

  try {
    // Accepting with typed text or edited prices must not discard them —
    // route through the modify endpoint so observations are saved (status:
    // modified) and price edits are persisted (status: complete).
    const hasEdits = hasChanges.value;
    if (mode === "accept" && !hasEdits) {
      await api.case.acceptEmail(caseid, token, { resourceIds });
    } else {
      const payload: any = {
        resourceIds,
      };

      const items = buildSavePayload();
      if (items.length) {
        payload.items = items;
      }

      const observation = supplierObservation.value.trim();
      if (observation) {
        payload.observations = observation;
      }

      await api.case.modificationEmail(caseid, token, payload);
    }
    sucessEmailUpdated.value = true;
    showSummary.value = false;
  } catch (error: any) {
    console.error(error);
    sucessEmailUpdated.value = false;
    showSummary.value = false;
    toast.error(error?.response?.data?.message || t("general.shared.errorMessage"));
  }
};

const getStatusClass = (status: string) => {
  const s = status ? status.toLowerCase() : "";

  if (s === 'confirmed' || s === 'complete' || s === 'paid' || s === 'received') return 'bg-success-soft text-success';
  if (s === 'pending' || s === 'requested' || s === 'partial') return 'bg-warning-soft text-warning';
  if (s === 'cancelled' || s === 'overdue' || s === 'denied') return 'bg-danger-soft text-danger';

  return 'bg-secondary-soft text-secondary';
};

const getStatusIcon = (status: string) => {
  const s = status ? status.toLowerCase() : "";

  if (s === 'confirmed' || s === 'complete' || s === 'paid' || s === 'received') return 'check';
  if (s === 'pending' || s === 'requested' || s === 'partial') return 'info';
  if (s === 'cancelled' || s === 'overdue' || s === 'denied') return 'x-circle';

  return 'info';
};
</script>

<style lang="scss" scoped>
.text-xs { font-size: 0.75rem; }
.text-xxs { font-size: 0.65rem; }
.text-sm { font-size: 0.875rem; }
.bg-light-soft { background-color: #f1f3f5; }
.bg-success-soft { background-color: rgba(25, 135, 84, 0.1); }
.bg-warning-soft { background-color: rgba(255, 193, 7, 0.1); }
.bg-danger-soft { background-color: rgba(220, 53, 69, 0.1); }
.bg-secondary-soft { background-color: rgba(108, 117, 125, 0.1); }

.group-tbody {
  td {
    padding: 4px 12px !important;
    vertical-align: middle;
    line-height: 1.2;

    div {
      margin: 0 !important;
    }
  }
}

.table-compact {
  th {
    font-size: 0.7rem;
    letter-spacing: 0.05em;
    background-color: #f8f9fa;
  }

  tr:hover {
    background-color: rgba(0, 0, 0, 0.01);
  }
}

// Keep columns readable on narrow screens: the responsive wrapper
// scrolls horizontally instead of squeezing the table.
.modification-table {
  min-width: 860px;
}

.summary-table {
  min-width: 560px;
}

.sub-table {
  min-width: 0;
  border-top: 1px dashed #dee2e6;

  td {
    padding: 4px 12px !important;
    vertical-align: middle;
  }
}

.badge-status-dot {
  width: 28px;
  height: 28px;
  border-radius: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.65rem;
  transition: all 0.2s;
  cursor: help;

  &:hover {
    transform: scale(1.1);
  }
}

.price-edit-wrapper {
  position: relative;
  width: 100px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  border: 1px solid transparent;
  border-radius: 0;
  padding: 2px 4px;
  transition: all 0.2s;

  &.is-editing {
    background-color: white;
    border-color: #dee2e6;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  }

  &.is-updated {
    background-color: rgba(220, 53, 69, 0.1);
  }
}

.price-v2-input {
  width: 100%;
  border: none;
  background: transparent !important;
  text-align: right;
  font-weight: 700;
  font-size: 0.875rem;
  color: #212529;
  padding: 2px 0;
  padding-right: 24px;
  outline: none !important;
  box-shadow: none !important;

  &[readonly] {
    cursor: default;
  }
}

.btn-edit-price {
  position: absolute;
  right: 4px;
  top: 50%;
  transform: translateY(-50%);
  background: transparent;
  border: none;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #0d6efd;
  opacity: 1;
  transition: all 0.2s;

  &:hover {
    color: #0250c5;
  }
}

.expander-btn {
  background: transparent;
  border: none;
  padding: 0;
  display: flex;
  align-items: center;
  color: #6c757d;

  &:hover {
    color: #212529;
  }
}

.supplier-avatar {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
}

.label-xs {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  color: #495057;
}

.observation-input {
  font-size: 0.875rem;
}

.observation-preview {
  white-space: pre-wrap;
  background-color: #f8f9fa;
  border-radius: 6px;
  padding: 0.75rem;
}

.is-updated-row {
  background-color: rgba(220, 53, 69, 0.05);
}

.btn-accept {
  background-color: #26D0A1 !important;
  border-color: #26D0A1 !important;
  color: #FFFFFF !important;

  &:hover {
    background-color: #1eb88c !important;
    border-color: #1eb88c !important;
  }
}

.btn-save-confirm {
  background-color: #26D0A1 !important;
  border-color: #26D0A1 !important;
  color: #FFFFFF !important;

  &:hover {
    background-color: #1eb88c !important;
    border-color: #1eb88c !important;
  }
}

.btn-discard {
  background-color: transparent !important;
  border-color: #6c757d !important;
  color: #6c757d !important;

  &:hover {
    background-color: #6c757d !important;
    color: #FFFFFF !important;
  }
}
</style>
